import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { useAppNavigate } from '../contexts/PwaNavContext';
import { setStoredLang, type LangCode } from '../i18n';
import './SettingsPage.css';

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useAppNavigate();
  const { customer, token, updateCustomer } = useCustomerAuth();

  const [username, setUsername] = useState(customer?.username || '');
  const [usernameError, setUsernameError] = useState('');
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [usernameSaved, setUsernameSaved] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const originalUsername = customer?.username || '';

  useEffect(() => {
    setUsername(customer?.username || '');
  }, [customer?.username]);

  const handleUsernameChange = (raw: string) => {
    const clean = raw.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 20);
    setUsername(clean);
    setUsernameError('');
    setUsernameSaved(false);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (clean.length < 3 || clean === originalUsername) return;

    debounceRef.current = setTimeout(async () => {
      setUsernameChecking(true);
      try {
        const { data } = await axios.get(`${API_URL || ''}/api/friends/check-username/${clean}`);
        if (!data.available) {
          setUsernameError(data.reason === 'invalid'
            ? t('username.invalid', 'Invalid format')
            : t('username.taken', 'Already taken'));
        }
      } catch { /* */ }
      setUsernameChecking(false);
    }, 400);
  };

  const handleUsernameSave = async () => {
    if (!USERNAME_RE.test(username) || !token || username === originalUsername) return;
    setUsernameSaving(true);
    try {
      await axios.post(
        `${API_URL || ''}/api/friends/set-username`,
        { username },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateCustomer({ username });
      setUsernameSaved(true);
      setTimeout(() => setUsernameSaved(false), 2000);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) && err.response?.data?.error;
      setUsernameError(msg || t('username.error', 'Could not save'));
    }
    setUsernameSaving(false);
  };

  const usernameValid = USERNAME_RE.test(username) && !usernameError && !usernameChecking && username !== originalUsername;

  return (
    <div className="sp-page">
      <div className="sp-header">
        <button className="sp-back" onClick={() => navigate('/')} aria-label="Back">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
        </button>
        <div className="sp-header-title">{t('settings.title', 'Settings')}</div>
        <div style={{ width: 36 }} />
      </div>

      <div className="sp-content">
        {/* ── Profile ── */}
        <div className="sp-section">
          <div className="sp-section-title">{t('settings.profile', 'Profile')}</div>

          <div className="sp-field">
            <div className="sp-field-label">{t('settings.name', 'Name')}</div>
            <div className="sp-field-value">{customer?.name || '—'}</div>
          </div>

          <div className="sp-field">
            <div className="sp-field-label">{t('settings.username', 'Username')}</div>
            <div className="sp-username-row">
              <div className="sp-username-input-wrap">
                <span className="sp-at">@</span>
                <input
                  className="sp-username-input"
                  value={username}
                  onChange={e => handleUsernameChange(e.target.value)}
                  placeholder="username"
                  maxLength={20}
                  autoCapitalize="off"
                  autoCorrect="off"
                />
                {usernameChecking && <span className="sp-spinner" />}
              </div>
              <button
                className="sp-save-btn"
                disabled={!usernameValid || usernameSaving}
                onClick={handleUsernameSave}
              >
                {usernameSaved ? '' : usernameSaving ? '...' : t('settings.save', 'Save')}
              </button>
            </div>
            {usernameError && <div className="sp-field-error">{usernameError}</div>}
            {username.length > 0 && username.length < 3 && !usernameError && (
              <div className="sp-field-hint">{t('username.minLength', 'Minimum 3 characters')}</div>
            )}
          </div>

          <div className="sp-field">
            <div className="sp-field-label">{t('settings.email', 'Email')}</div>
            <div className="sp-field-value">{customer?.email || '—'}</div>
          </div>
        </div>

        {/* ── Language ── */}
        <div className="sp-section">
          <div className="sp-section-title">{t('settings.language', 'Language')}</div>
          <div className="sp-lang-options">
            {([
              { code: 'ru' as LangCode, label: 'Русский' },
              { code: 'en' as LangCode, label: 'English' },
              { code: 'kk' as LangCode, label: 'Қазақша' },
            ]).map(({ code, label }) => (
              <button
                key={code}
                className={`sp-lang-btn ${i18n.language === code ? 'sp-lang-btn--active' : ''}`}
                onClick={() => setStoredLang(code)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
